import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildSyncSegments, findActiveSegmentIndex } from '../utils/audioSync';

const useAudioSync = ({ content, durationSeconds, apiSegments, audioUrl }) => {
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(-1);

  const segments = useMemo(
    () => buildSyncSegments(content, durationSeconds, apiSegments),
    [content, durationSeconds, apiSegments]
  );

  useEffect(() => {
    setActiveSegmentIndex(findActiveSegmentIndex(segments, currentTime));
  }, [currentTime, segments]);

  const seekTo = useCallback((seconds) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, seconds);
    setCurrentTime(audio.currentTime);
  }, []);

  const seekToSegment = useCallback(
    (index) => {
      const segment = segments[index];
      if (segment) seekTo(segment.start);
    },
    [segments, seekTo]
  );

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (audio) setCurrentTime(audio.currentTime);
  }, []);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;
    if (audio.paused) {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch {
        setIsPlaying(false);
      }
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  }, [audioUrl]);

  return {
    audioRef,
    audioUrl,
    segments,
    currentTime,
    activeSegmentIndex,
    isPlaying,
    seekTo,
    seekToSegment,
    handleTimeUpdate,
    togglePlay,
    setIsPlaying,
  };
};

export default useAudioSync;
